<template>
    <!--
        What stands in place of the whole portal when the hotel has not bought the
        module behind it. One screen and no navigation: every tab underneath leads to
        the same refusal, and a "try again" would only be a way of asking the same
        locked door the same question.

        The one control is not a retry. Signing out is the single thing that can be
        done from this device that changes the answer — the account is what the API
        was asked about — and without it a shared tablet stayed locked on the first
        housekeeper's refusal for everybody after her.
    -->
    <div class="grid min-h-dvh place-items-center bg-muted p-6">
        <div class="w-full max-w-md text-center">
            <div class="mx-auto grid size-14 place-items-center rounded-full bg-elevated">
                <UIcon name="i-lucide-lock" class="size-7 text-dimmed" />
            </div>

            <h1 class="mt-5 text-2xl font-bold text-highlighted">{{ $t('license.title') }}</h1>

            <!-- The house, where the session got far enough to learn its name. It is
                 the one fact that tells a supervisor whether they are looking at the
                 hotel they meant. -->
            <p v-if="hotel" class="mt-1 text-sm font-medium text-toned">{{ hotel }}</p>

            <p class="mt-4 text-sm text-muted">{{ $t('license.lead') }}</p>

            <UButton
                :label="$t('license.switch_account')"
                icon="i-lucide-log-out"
                color="neutral"
                variant="subtle"
                size="lg"
                block
                class="mt-6"
                @click="switchAccount"
            />

            <p class="mt-5 text-xs text-dimmed">{{ $t('license.contact') }}</p>
        </div>
    </div>
</template>

<script lang="ts" setup>
    import { computed } from 'vue'
    import { useRouter } from 'vue-router'

    import { useAppStore } from '@/stores/app'

    const router   = useRouter()
    const appStore = useAppStore()

    // Empty is the ordinary case, not a failure: the property list is itself a call
    // this refusal may have stopped. The line is then simply not drawn.
    const hotel = computed((): string => appStore.getProperty()?.name ?? '')

    /*
     * Out of this account and back to the sign-in screen.
     *
     * `setLogout` is what clears the refusal — it is cleared where the session ends
     * and not here, so that every way out of a session (the topbar's button, an
     * expired token, the router guard) forgets it too. This screen only asks for the
     * one that a person standing in front of it can ask for.
     */
    const switchAccount = () => {
        appStore.setLogout()
        void router.push({ name: 'login' })
    }
</script>
